import { z } from "zod";

import { DbEnum } from "../../../../generated/prisma-enums";
import type { AITaskMetadataSnapshot } from "../../../../shared/domain/tools/ai-task-version.types";

/**
 * AI Task Version schemas — checkpoints & auto-saves for AI tasks.
 * Mirrors the agent prompt versioning schemas (agent-versions domain);
 * the metadata snapshot replaces promptJson as the versioned payload.
 */

export const AITaskVersionTypeSchema = z.enum(DbEnum.PROMPT_VERSION_TYPE_VALUES);

export const AITaskVersionCreatorSchema = z.object({
  id: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable()
});

export type AITaskVersionCreator = z.infer<typeof AITaskVersionCreatorSchema>;

/**
 * Version summary used in list responses and mutation responses.
 * No metadata snapshot — use AITaskVersionDetailSchema for full content.
 */
export const AITaskVersionMetadataSchema = z.object({
  id: z.string(),
  type: AITaskVersionTypeSchema,
  name: z.string().nullable(),
  description: z.string().nullable(),
  // ISO string — controllers serialize Date before wrapping
  createdAt: z.string(),
  createdBy: z.string().nullable(),
  creator: AITaskVersionCreatorSchema.nullable(),
  isProduction: z.boolean()
});

export type AITaskVersionMetadata = z.infer<typeof AITaskVersionMetadataSchema>;

/**
 * Full version data including the metadata snapshot.
 * Used for getVersion responses.
 */
export const AITaskVersionDetailSchema = AITaskVersionMetadataSchema.extend({
  // z.custom: snapshot shape is produced by trusted builders on both sides
  // (buildMetadataSnapshot / buildAITaskMetadataSnapshot); no structural validation
  metadata: z.custom<AITaskMetadataSnapshot>()
});

export type AITaskVersionDetail = z.infer<typeof AITaskVersionDetailSchema>;

export const AITaskVersionListResponseSchema = z.object({
  versions: z.array(AITaskVersionMetadataSchema),
  total: z.number(),
  /** Count of CHECKPOINT versions — used for auto-naming new versions (v1, v2, v3...) */
  checkpointCount: z.number()
});

export type AITaskVersionListResponse = z.infer<typeof AITaskVersionListResponseSchema>;

/** Lightweight status for the task editor header. */
export const AITaskVersionStatusSchema = z.object({
  checkpointCount: z.number(),
  productionVersionName: z.string().nullable(),
  /** true = current draft is the production version (productionVersionId is null) */
  isDraftProduction: z.boolean()
});

export type AITaskVersionStatus = z.infer<typeof AITaskVersionStatusSchema>;
